import { TextFieldProps } from '@mui/material'
import {
  ContainerDto,
  ContainerHeight,
  ContainerResponseDto,
  ContainerType,
  CreateContainerVisitCommand,
  RemainOnBoardUnitUpsertedDto,
  UnitType,
  UpsertContainerCommand,
} from '@planning/app/api'
import { OrderHoldsSectionStore } from '@planning/components/hold/organisms/stores/OrderHoldsSectionStore'
import { ContainerItemStore } from '@planning/rt-stores/container/ContainerItemStore'
import { containerService } from '@planning/services'
import companyService from '@planning/services/companyService'
import containerJourneysService from '@planning/services/containerJourneys'
import holdService from '@planning/services/holdService'
import restowService from '@planning/services/restowService'
import { FieldStore } from '@tom-ui/utils'
import { action, makeObservable, observable } from 'mobx'
import { OrderFormStore } from '../V2/stores/OrderFormStore'
import { StepperFormViewStore } from '../components/StepperForm/StepperFormViewStore'
import { ContainerCreationFormData } from '../components/StepperForm/stores/ContainerCreationFormStore'
import { OrderReviewStore } from '../components/StepperForm/stores/OrderReviewStore'
import { SwapBodyCreationFormData } from '../components/StepperForm/stores/SwapBodyCreationFormStore'
import { TrailerCreationFormData } from '../components/StepperForm/stores/TrailerCreationFormStore'
import { OrderPageViewStore } from './OrderPageViewStore'

export type IsoCodeMappingFieldProps = {
  field: FieldStore<string>
  label?: string
  fullWidth?: boolean
  disabled?: boolean
  required?: boolean
  variant?: TextFieldProps['variant']
  inputGridSizes?: IsoCodeMappingFieldGridSizes
  messageGridSizes?: IsoCodeMappingFieldGridSizes
}

type IsoCodeMappingFieldGridSizes = {
  xs?: number
  sm?: number
  md?: number
}

export type isoCodeMappingFieldRenderFnType = (
  props: IsoCodeMappingFieldProps,
) => React.ReactElement

export class CreateOrderPageViewStore extends OrderPageViewStore {
  carrierVisitId?: number
  containerId?: number
  container?: ContainerDto | ContainerResponseDto
  subFormsToValidate: { [key: number]: (() => boolean)[] } = {}
  isoCodeMappingFieldRenderFn?: isoCodeMappingFieldRenderFnType
  isFromRestowPage?: boolean
  containerRemainOnBoardReference?: RemainOnBoardUnitUpsertedDto
  holdsStore = new OrderHoldsSectionStore()

  constructor(
    public formStore: OrderFormStore,
    public stepperFormViewStore: StepperFormViewStore,
    public orderReviewStore: OrderReviewStore,
    public containerItemStore: ContainerItemStore,
    isoCodeMappingFieldRenderFn?: isoCodeMappingFieldRenderFnType,
    isFromRestowPage?: boolean,
    carrierVisitId?: number,
    containerId?: number,
    public inheritedOperatorShortName?: string,
  ) {
    super()
    makeObservable(this, {
      containerId: observable,
      container: observable,
      subFormsToValidate: observable,
      containerRemainOnBoardReference: observable,

      submit: action,
      createUnit: action,
      setContainerId: action,
      setContainer: action,
      reset: action,
      registerStepValidators: action,
      validateCurrentStep: action,
      selectContainerById: action,
    })

    this.isoCodeMappingFieldRenderFn = isoCodeMappingFieldRenderFn
    this.isFromRestowPage = isFromRestowPage
    this.carrierVisitId = carrierVisitId

    if (containerId) {
      this.selectContainerById(containerId)
    }
  }

  registerStepValidators(step: number, validators: (() => boolean)[]) {
    this.subFormsToValidate[step] = validators
  }

  validateCurrentStep(): boolean {
    const validators = this.subFormsToValidate[this.stepperFormViewStore.activeStep] ?? []

    return validators.every(validate => validate())
  }

  async createUnit() {
    try {
      const cmd = this.mapToUnit()

      const containerId = await containerService.post(cmd)
      const containers = await containerService.getByIds([containerId])
      const container = containers[0]

      this.selectContainer({ ...container, activeOrderIds: [] })
      this.stepperFormViewStore.next()
    } catch (error) {
      console.error('Failed to create unit:', error)
    }
  }

  async selectContainerById(id: number) {
    const containers = await containerService.getByIds([id])
    if (containers) {
      await this.selectContainer(containers[0])
    }
  }

  async selectContainer(container: ContainerDto | ContainerResponseDto) {
    this.setContainer(container)
    if (container.id) this.setContainerId(container.id)

    if (container.number) this.fetchRemainOnBoardData(container.number)

    if (container?.operator) {
      const company = await companyService.getByShortName(container.operator)
      this.formStore.customer.setValue(company)
    }
  }

  setContainerId(id: number) {
    this.containerId = id
  }

  setContainer(data: ContainerDto | ContainerResponseDto) {
    this.container = data
  }

  async submit() {
    const cmd = this.mapToCreateContainerVisitCommand()
    const data = await containerService.createContainerVisit(cmd.containerId as number, cmd)

    return data
  }

  async validateAndChangeStep(step?: number) {
    const isValid = this.validateCurrentStep()
    const isStep = step || step === 0

    if (!isValid) {
      if (isStep && this.stepperFormViewStore.activeStep > step) {
        this.stepperFormViewStore.goTo(step)
      }
      return
    }

    if (this.stepperFormViewStore.isFirstStep && !this.containerId) {
      await this.createUnit()
    } else {
      if (isStep) {
        this.stepperFormViewStore.goTo(step)
      } else {
        this.stepperFormViewStore.next()
      }
    }

    const isReviewStep =
      this.stepperFormViewStore.activeStep === this.stepperFormViewStore.totalSteps - 1

    if (isReviewStep) {
      this.orderReviewStore.setData(this.formStore.data)
    }
  }

  mapToCreateContainerVisitCommand(): CreateContainerVisitCommand {
    const formData = this.formStore.data

    const commonCargoData = formData.cargoDetails

    return {
      containerId: this.containerId ?? 0,
      containerNumber: this.container?.number ?? '',
      containerIsoCode: this.container?.isoCode ?? '',
      referenceNumber: formData.referenceNumber,
      notes: formData.notes,
      operationalInstructions: formData.operationalInstructions,
      consignee: formData.consignee,
      customerId: formData.customer?.id,
      inboundTransportData: this.mapToTransportData(formData.inboundCargoDetails, commonCargoData),
      outboundTransportData: this.mapToTransportData(
        formData.outboundCargoDetails,
        commonCargoData,
      ),
      inboundCargo: this.mapToCargoData(commonCargoData),
      outboundCargo: this.mapToCargoData(commonCargoData),
      inboundHasSeals: commonCargoData?.seals?.hasSeals,
      outboundHasSeals: commonCargoData?.seals?.hasSeals,
      inboundSeals: commonCargoData?.seals?.seals,
      outboundSeals: commonCargoData?.seals?.seals,
      outboundCustoms: commonCargoData?.customs,
      stowageInstructionId: commonCargoData?.stowageInstruction?.hasStowageInstruction
        ? commonCargoData?.stowageInstruction?.id
        : null,

      inlandDestination: formData.inlandDestination,
      finalDestination: formData.finalDestination,

      portOfDischarge: formData.portOfDischarge,
      portOfLoading: formData.portOfLoading,

      placeOfOrigin: formData?.origin,

      shipperId: formData.shipperId,
      forwarderId: formData.forwarderId,
      billOfLading: formData.billOfLadding,
      shippingOrderReference: formData.shippingOrder,
      releaseReference: formData.releaseReference,

      isApprovedOutbound: formData.loadApproval,
      isApprovedInbound: formData.dischargeApproval,
      holds: this.holdsStore.upsertPayload,
    }
  }

  inferUnitTypeFromData(data: any): UnitType {
    if ('containerNumber' in data) return UnitType.Container
    if ('iluCode' in data) return UnitType.Trailer
    if ('unitNumber' in data) return UnitType.SwapBody
    throw new Error('Invalid unitInfo structure')
  }

  mapToUnit(): UpsertContainerCommand {
    const unitInfo = this.formStore.data.unitInfo
    if (!unitInfo) throw new Error('No unit data')

    const unitType = this.inferUnitTypeFromData(unitInfo)

    switch (unitType) {
      case UnitType.Container:
        return this.mapContainerToUnit(unitInfo as ContainerCreationFormData)
      case UnitType.SwapBody:
        return this.mapSwapBodyToUnit(unitInfo as SwapBodyCreationFormData)
      case UnitType.Trailer:
        return this.mapTrailerToUnit(unitInfo as TrailerCreationFormData)
      default:
        throw new Error('Unknown unitType')
    }
  }

  private mapContainerToUnit(data: ContainerCreationFormData): UpsertContainerCommand {
    return {
      number: data.containerNumber,
      operator: data.operator || null,
      isoCode: data.isoCode || null,
      tare: data.tare ? Number(data.tare) : null,
      maxGross: data.maxGross ? Number(data.maxGross) : null,
      unitType: UnitType.Container,
    }
  }

  private mapSwapBodyToUnit(data: SwapBodyCreationFormData): UpsertContainerCommand {
    return {
      number: data.unitNumber,
      operator: data.operator || null,
      type: data.type as ContainerType,
      height: data.height as ContainerHeight,
      length: data.length,
      unitType: UnitType.SwapBody,
    }
  }

  private mapTrailerToUnit(data: TrailerCreationFormData): UpsertContainerCommand {
    return {
      number: data.iluCode,
      operator: data.operator || null,
      isoCode: null,
      tare: null,
      maxGross: null,
      type: data.type as ContainerType,
      length: data.length,
      plateNumber: data.plateNumber,
      unitType: UnitType.Trailer,
    }
  }

  private async fetchRemainOnBoardData(containerNumber: string) {
    if (!this.carrierVisitId || !this.isFromRestowPage) return

    const remainOnBoardDto = await restowService.getRemainOnBoardByVisitAndContainer(
      this.carrierVisitId,
      containerNumber,
    )

    this.containerRemainOnBoardReference = remainOnBoardDto

    if (!remainOnBoardDto) return

    this.formStore.referenceNumber.setValue(remainOnBoardDto.referenceNumber ?? '')
    this.formStore.portOfDischarge.setValue(remainOnBoardDto.portOfDischarge ?? '')
    this.formStore.portOfLoading.setValue(remainOnBoardDto.portOfLoading ?? '')
  }

  resetStepValidators(step: number) {
    delete this.subFormsToValidate[step]
  }

  reset() {
    this.containerId = undefined
    this.container = undefined
    this.resetStepValidators(0)
  }
}
